import React, { useCallback, useContext, useEffect, useState } from 'react';
import Box from '@rexlabs/box';
import { styled, Styles } from '@rexlabs/styling';
import { api } from 'utils/api-client';
import { SnackbarContext } from 'src/features/reports/providers/snackbar-provider';
import {
  TimeOnMarketBySizeBandProject,
  SizeBandData,
  LotTimeOnMarket
} from 'types/graph';
import { defaultStyles, SIZE_BANDS } from './time-on-market.styles';

type Tab = 'size-band' | 'lot-detail';

function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 60; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      '0'
    )}-01`;
    const label = d.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short'
    });
    options.push({ value, label });
  }
  return options;
}

const MONTH_OPTIONS = getMonthOptions();

function getDefaultStartDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

interface Props {
  styles: Styles;
}

function TimeOnMarketScreen({ styles: s }: Props) {
  const { addMessage } = useContext(SnackbarContext);
  const [activeTab, setActiveTab] = useState<Tab>('size-band');
  const [data, setData] = useState<TimeOnMarketBySizeBandProject[]>([]);
  const [lotData, setLotData] = useState<LotTimeOnMarket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshResult, setRefreshResult] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState('');

  const fetchData = useCallback(() => {
    setIsLoading(true);
    setError(null);

    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const qs = new URLSearchParams(params).toString();

    if (activeTab === 'size-band') {
      const url = '/reports/time-on-market/by-size-band' + (qs ? `?${qs}` : '');
      api
        .get(url)
        .then((res) => {
          setData(Array.isArray(res.data) ? res.data : []);
          setIsLoading(false);
        })
        .catch((err) => {
          const serverMsg: string = err?.response?.data?.message ?? '';
          if (serverMsg.includes('has not been populated')) {
            addMessage(
              'The data has not been populated yet. Click "Refresh Views" to load the latest data.'
            );
          } else {
            setError(err?.message || 'Failed to load data');
          }
          setIsLoading(false);
        });
    } else {
      const url = '/reports/time-on-market/lots' + (qs ? `?${qs}` : '');
      api
        .get(url)
        .then((res) => {
          setLotData(Array.isArray(res.data) ? res.data : []);
          setIsLoading(false);
        })
        .catch((err) => {
          const serverMsg: string = err?.response?.data?.message ?? '';
          if (serverMsg.includes('has not been populated')) {
            addMessage(
              'The data has not been populated yet. Click "Refresh Views" to load the latest data.'
            );
          } else {
            setError(err?.message || 'Failed to load data');
          }
          setIsLoading(false);
        });
    }
  }, [startDate, endDate, activeTab, addMessage]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setRefreshResult(null);
    api
      .post('/reports/time-on-market/refresh')
      .then((res) => {
        const d = res.data || res;
        setRefreshResult(
          `Refreshed: ${d.lot_count} lots, ${d.summary_count} summary rows`
        );
        setIsRefreshing(false);
        fetchData();
      })
      .catch((err) => {
        setRefreshResult(`Refresh failed: ${err?.message || 'Unknown error'}`);
        setIsRefreshing(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filterLabel =
    startDate || endDate
      ? `Showing lots sold${startDate ? ` from ${startDate}` : ''}${
          endDate ? ` to ${endDate}` : ''
        }`
      : 'Showing all time (no date filter)';

  return (
    <Box flexDirection="column" p="24px">
      <Box alignItems="center" mb="16px">
        <h2>Time on Market</h2>
        <Box ml="16px">
          <button onClick={handleRefresh} disabled={isRefreshing}>
            {isRefreshing ? 'Refreshing...' : 'Refresh Views'}
          </button>
        </Box>
        {refreshResult && (
          <Box ml="8px">
            <span>{refreshResult}</span>
          </Box>
        )}
      </Box>

      <Box alignItems="center" mb="16px">
        <button
          {...s(activeTab === 'size-band' ? 'tabButtonActive' : 'tabButton')}
          onClick={() => setActiveTab('size-band')}
          style={{ borderRadius: '4px 0 0 4px' }}
        >
          By Size Band
        </button>
        <button
          {...s(activeTab === 'lot-detail' ? 'tabButtonActive' : 'tabButton')}
          onClick={() => setActiveTab('lot-detail')}
          style={{ borderRadius: '0 4px 4px 0' }}
        >
          Lot Detail
        </button>
      </Box>

      <Box alignItems="center" mb="16px">
        <span {...s('filterLabel')}>Start:</span>
        <select
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        >
          <option value="">All time</option>
          {MONTH_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <Box ml="16px">
          <span {...s('filterLabel')}>End:</span>
          <select value={endDate} onChange={(e) => setEndDate(e.target.value)}>
            <option value="">All time</option>
            {MONTH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Box>

        <Box ml="16px">
          <span>
            {filterLabel} —{' '}
            {activeTab === 'size-band'
              ? `${data.length} projects`
              : `${lotData.length} lots`}
          </span>
        </Box>
      </Box>

      {isLoading && <p>Loading...</p>}
      {error && <p>Error: {error}</p>}

      {!isLoading && !error && activeTab === 'size-band' && (
        <div>
          <div {...s('header')}>
            <div {...s('cellLeft')}>Project</div>
            {SIZE_BANDS.map((band) => (
              <div key={band} {...s('cell')}>
                {band} sqm
              </div>
            ))}
          </div>

          {data.length === 0 && (
            <div {...s('row')}>
              <div {...s('emptyRow')}>No data for the selected period.</div>
            </div>
          )}

          {data.map((project) => (
            <div key={project.project_id} {...s('row')}>
              <div {...s('cellLeft')}>{project.project_name}</div>
              {SIZE_BANDS.map((band) => {
                const bandData: SizeBandData | null = project.size_bands[band];
                return (
                  <div key={band} {...s('cell')}>
                    {bandData ? (
                      <Box flexDirection="column" alignItems="center">
                        <span {...s('avgDays')}>{bandData.avg_days}d</span>
                        <span {...s('soldCount')}>
                          {bandData.sold_count} sold
                        </span>
                      </Box>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {!isLoading && !error && activeTab === 'lot-detail' && (
        <div>
          <div {...s('lotHeader')}>
            <div {...s('cellLeft')}>Project</div>
            <div {...s('cell')}>Lot #</div>
            <div {...s('cell')}>Dimension</div>
            <div {...s('cellRight')}>Size (sqm)</div>
            <div {...s('cell')}>Size Band</div>
            <div {...s('cellRight')}>Price</div>
            <div {...s('cell')}>On Market</div>
            <div {...s('cell')}>Sold</div>
            <div {...s('cellRight')}>Days</div>
          </div>

          {lotData.length === 0 && (
            <div {...s('lotRow')}>
              <div {...s('emptyRow')}>No data for the selected period.</div>
            </div>
          )}

          {lotData.map((lot, idx) => (
            <div
              key={`${lot.lot_number}-${lot.sold_date}-${idx}`}
              {...s('lotRow')}
            >
              <div {...s('cellLeft')}>{lot.project_name}</div>
              <div {...s('cell')}>{lot.lot_number}</div>
              <div {...s('cell')}>{lot.lot_dimension || '—'}</div>
              <div {...s('cellRight')}>{lot.lot_size}</div>
              <div {...s('cell')}>{lot.size_band}</div>
              <div {...s('cellRight')}>{formatCurrency(lot.price)}</div>
              <div {...s('cell')}>{lot.on_market_date || '—'}</div>
              <div {...s('cell')}>{lot.sold_date}</div>
              <div {...s('cellRight')}>{lot.time_on_market_days}</div>
            </div>
          ))}
        </div>
      )}
    </Box>
  );
}

export default styled(defaultStyles)(TimeOnMarketScreen);
